/* eslint-disable no-console */
import { createContext, PropsWithChildren, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

import { selectUser } from 'store';

import { useAppSelector } from 'hooks';
import { errorCallBack, socketReconnect, SocketResProps } from 'socket';
import { PathName } from 'router/paths';
import { stripBasePath } from 'utils/basePath';
import { groupMessagesByDate, mergeChannelsById, updateChannelInList } from 'utils/messages';

import {
  ChannelDetailsProps,
  ChannelProps,
  DateMessagesProps,
  GetOldMessageParams,
  MarkSeenParams,
  MessageProps,
  MessagesContextParams,
  NewChannelCreatedProps,
  NewMessageToServerParams,
  TotalMessageCountUpdateProps,
  UnreadMessageUpdatedProps
} from './messagesContext.types';

export const MessagesContext = createContext<MessagesContextParams>({
  channelDetails: null,
  channels: [],
  clearChannels: () => {},
  closeChannel: () => {},
  dateMessages: null,
  generalHealth: '',
  getChannels: () => {},
  getOldMessage: () => {},
  isLoading: true,
  joinRoom: () => {},
  markSeen: () => {},
  sendMessage: () => {},
  totalMessageCountUpdate: null
});

export const MessagesProvider: React.FC<PropsWithChildren<{ socket: Socket }>> = ({
  children,
  socket
}) => {
  const { refreshToken } = useAppSelector(selectUser);
  const refreshTokenRef = useRef(refreshToken);
  const [generalHealth, setGeneralHealth] = useState<string>('');
  const [markedSeenMessage, setMarkedSeenMessage] = useState<MessageProps | null>(null);
  const [totalMessageCountUpdate, setTotalMessageCountUpdate] =
    useState<TotalMessageCountUpdateProps | null>(null);
  const [newChannelCreated, setNewChannelCreated] = useState<NewChannelCreatedProps | null>(null);
  const [messageToClients, setMessageToClients] = useState<MessageProps | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [listOfMessages, setListOfMessages] = useState<MessageProps[] | null>(null);
  const [dateMessages, setDateMessages] = useState<DateMessagesProps[] | null>(null);
  const [channels, setChannels] = useState<ChannelProps[]>([]);
  const [channelDetails, setChannelDetails] = useState<ChannelDetailsProps | null>(null);
  const [unreadMessageUpdated, setUnreadMessageUpdated] =
    useState<UnreadMessageUpdatedProps | null>(null);
  const [oldMessages, setOldMessages] = useState<MessageProps[]>([]);
  const [historyCatchUp, setHistoryCatchUp] = useState<MessageProps[] | null>(null);
  const [skip, setSkip] = useState(1);

  const closeChannel = () => {
    setOldMessages([]);
    setDateMessages(null);
    setChannelDetails(null);
  };

  const joinRoom = (channelId: string) => {
    socket.emit('joinRoom', channelId, (error: SocketResProps) => errorCallBack('joinRoom', error));
  };

  const markSeen = (params: MarkSeenParams) => {
    socket.emit('markSeen', params, (error: SocketResProps) => errorCallBack('markSeen', error));
  };

  const handleSetOldMessages = (data: MessageProps[]) => {
    setIsLoading(false);
    setOldMessages(data);
  };

  const handleUpdatedOldMessages = () => {
    if (oldMessages.length) {
      setHistoryCatchUp([...oldMessages, ...(listOfMessages || [])]);
    }
  };

  const updateChannel = (data: {
    channelId: string;
    latestMessage?: string;
    latestMessageDate?: Date;
    unreadMessageCount?: number;
  }) => {
    setChannels((prev) => updateChannelInList(prev, data));
  };

  const handleUpdatedHistoryCatchUp = () => {
    if (!channelDetails || !historyCatchUp) return;

    const blurredHistory = historyCatchUp.map((el) => ({ ...el, isBlur: true }));
    const filteredData = blurredHistory.filter(
      (message) => message.channelId === channelDetails._id
    );

    if (filteredData.length || !historyCatchUp.length) {
      setListOfMessages(filteredData);
    }
  };

  const handleUpdatedUnreadMessageUpdated = () => {
    if (unreadMessageUpdated) {
      updateChannel(unreadMessageUpdated);
    }
  };

  const handleUpdatedMessageToClients = () => {
    if (messageToClients) {
      setListOfMessages((prev) => [...(prev || []), messageToClients]);
      updateChannel({
        channelId: messageToClients.channelId,
        latestMessage: messageToClients.message,
        latestMessageDate: messageToClients.messageStatus.sent
      });
    }
  };

  const handleUpdatedListOfMessages = () => {
    if (!listOfMessages) return;

    setDateMessages(groupMessagesByDate(listOfMessages));
    setIsLoading(false);
  };

  const handleSetChannels = (items: ChannelProps[]) => {
    setIsLoading(false);
    setChannels((prev) => mergeChannelsById(prev, items));
  };

  const getOldMessage = (params: GetOldMessageParams, cb: () => void) => {
    if (isLoading) return;

    setIsLoading(true);
    socket.emit('getNextMessage', params, (error: SocketResProps) => {
      setIsLoading(false);
      errorCallBack('getNextMessage', error);
    });
    cb();
  };

  const getChannels = () => {
    socket.emit(
      'listChannels',
      {
        limit: 10,
        skip
      },
      (error: SocketResProps) => errorCallBack('listChannels', error)
    );
    setSkip((prev) => prev + 1);
  };

  const sendMessage = (params: NewMessageToServerParams) => {
    socket.emit(
      'newMessageToServer',
      params,
      stripBasePath(window.location.pathname) === PathName.ZoomCall,
      (error: SocketResProps) => errorCallBack('newMessageToServer', error)
    );
  };

  const clearChannels = () => setChannels([]);

  const handleUpdatedChannels = () => {
    if (channels.length) {
      setIsLoading(false);
      setGeneralHealth(
        channels.find(
          ({ channelTitle }) => channelTitle?.toLowerCase() === 'General health'.toLowerCase()
        )?.channelId || ''
      );
    }
  };

  const handleNewChannelCreated = () => {
    if (newChannelCreated && channels) {
      setChannels((prev) => [
        ...prev,
        {
          _id: newChannelCreated.patientDetail.id,
          channelDescription: newChannelCreated.channelDescription,
          channelId: newChannelCreated._id,
          channelTitle: newChannelCreated.channelTitle,
          latestMessageDate: newChannelCreated.latestMessageDate,
          patientId: newChannelCreated.patientDetail.id,
          unreadMessageCount: 0
        }
      ]);
    }
  };

  const handleUpdatedMarkedSeenMessage = () => {
    if (markedSeenMessage) {
      setListOfMessages((prev) => {
        if (!prev) return prev;

        return prev.map((item) =>
          item._id === markedSeenMessage._id
            ? {
                ...item,
                receiverDetails: markedSeenMessage.receiverDetails
              }
            : item
        );
      });
    }
  };

  useEffect(handleUpdatedOldMessages, [oldMessages]);

  useEffect(handleUpdatedMessageToClients, [messageToClients]);

  useEffect(handleUpdatedChannels, [channels]);

  useEffect(handleNewChannelCreated, [newChannelCreated]);

  useEffect(handleUpdatedMarkedSeenMessage, [markedSeenMessage]);

  useEffect(handleUpdatedUnreadMessageUpdated, [unreadMessageUpdated]);

  useEffect(handleUpdatedHistoryCatchUp, [historyCatchUp, channelDetails]);

  useEffect(handleUpdatedListOfMessages, [listOfMessages]);

  useEffect(() => {
    refreshTokenRef.current = refreshToken;
  }, [refreshToken]);

  useEffect(() => {
    socket.on('channels', handleSetChannels);
    socket.on('channelsList', handleSetChannels);
    socket.on('newChannelCreated', setNewChannelCreated);
    socket.on('markedSeen', setMarkedSeenMessage);
    socket.on('oldMessages', handleSetOldMessages);
    socket.on('historyCatchUp', setHistoryCatchUp);
    socket.on('channelDetails', setChannelDetails);
    socket.on('messageToClients', setMessageToClients);
    socket.on('unreadMessageUpdated', setUnreadMessageUpdated);
    socket.on('totalMessageCountUpdate', setTotalMessageCountUpdate);
    socket.on('connect', () => console.info('messages connect'));
    socket.on('connect_error', (err) => {
      if (err.message === 'AUTH_EXPIRED') {
        socketReconnect(socket, refreshTokenRef.current);
      }
    });
    socket.on('disconnect', () => console.info('messages disconnect'));
    return () => {
      socket.off('connect');
      socket.off('disconnect');
      socket.off('channels');
      socket.off('channelsList');
      socket.off('newChannelCreated');
      socket.off('markedSeen');
      socket.off('oldMessages');
      socket.off('historyCatchUp');
      socket.off('channelDetails');
      socket.off('messageToClients');
      socket.off('unreadMessageUpdated');
      socket.off('totalMessageCountUpdate');
      socket.off('connect');
      socket.off('connect_error');
    };
  }, []);

  return (
    <MessagesContext.Provider
      value={{
        channelDetails,
        channels,
        clearChannels,
        closeChannel,
        dateMessages,
        generalHealth,
        getChannels,
        getOldMessage,
        isLoading,
        joinRoom,
        markSeen,
        sendMessage,
        totalMessageCountUpdate
      }}
    >
      {children}
    </MessagesContext.Provider>
  );
};
